/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2017
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
/**
 * These static strings describe object creation. If an object did not exist yet - for versioned
 * objects that includes any previous versions based on the ID-object - the status is "new". If
 * an object that is to be created already exists (which is recognized because the names of all
 * files are based on the SHA256 crypto hash of its contents) the status is "exists". For
 * versioned objects this means the exact object already exists, not just a previous version
 * (base don ID-object). The last state is used for versioned objects only. When a previous
 * version of the object exists, based on the ID-object, but the exact version of the object
 * does not, the object is created and added as a new version of the existing ID-object.
 * *Note:* This is defined here and not in storage-unversioned-objects.js because then we
 * would have to import that file here, but since we already do it the other way around it's
 * easier to avoid a cyclic reference for such a minor thing, even though it works. **Always
 * use the names (keys) on this structure, never use the values themselves!**
 * @static
 * @type {object}
 * @property {'new'} NEW - There was not even a previous version of this object
 * @property {'exists'} EXISTS - This exact object (identified by SHA-256) already exists
 */
export const CREATION_STATUS = {
    NEW: 'new',
    EXISTS: 'exists'
};
/**
 * String constants for {@link SetAccessParam}'s `mode` parameter.
 * @static
 * @type {object}
 * @property {'replace'} REPLACE
 * @property {'add'} ADD
 */
export const SET_ACCESS_MODE = {
    REPLACE: 'replace',
    ADD: 'add'
};
/**
 * String constants for the storage types.
 * Avoid having to repeat the string constant
 * @static
 * @type {object}
 * @property {OBJECTS} "objects"
 * @property {PRIVATE} "private"
 */
export const STORAGE = {
    OBJECTS: 'objects',
    TMP: 'tmp',
    RMAPS: 'rmaps',
    VHEADS: 'vheads',
    ACACHE: 'acache',
    PRIVATE: 'private'
};
/**
 * Used to ensure createTempFileName() creates unique names.
 * @private
 * @type {number}
 */
let tempFileNameCounter = 0;
/**
 * Temporary filenames are needed for files we receive as streams, for example BLOBs received
 * during Chum exchange. We won't know the SHA-256 normally used as filename until we received it
 * completely.
 *
 * **NOTE:** This function cannot be replaced by system/crypto-helpers createRandomString. We
 * need this to be a synchronous function, or we would have to return SimpleWriteStream API
 * objects asynchronously (undesirable because illogical).
 * @static
 * @returns {string} A randomly created temporary filename
 */
export function createTempFileName() {
    // Using only the counter is sufficient because each instance, identified by its ID hash,
    // has its own storage space and in it its own "tmp" area. One would have to run two
    // versions of the same instance at the same time to get conflicts. Still, in case of
    // conflict with leftover files from a previous run we also use a timestamp. Also using the
    // counter avoids conflicts for multiple calls to this function within the same millisecond.
    // While there should not be any leftover tmp files from previous runs that is a higher
    // level issue that in this function we don't want to rely on, because that might very well
    // change. So let's just create a name that will still work if name conflicts across
    // multiple runs are possible, since it is so easy to avoid and since it removes a very
    // low-level dependency on high-level design decisions.
    return `tmp-${Date.now()}-${tempFileNameCounter++}`;
}
//# sourceMappingURL=storage-base-common.js.map